import {action, observable, runInAction} from 'mobx';
import googleOptimizeStore from '../../stores/GoogleOptimizeStore';
import manageStore from '../../stores/ManageStore';
import onBoardingStore from '../../containers/main/onBoarding/store';
import mainStore from '../main/store';
import {actions, category, DialogTypes, events, PromoType, StudioDrawerState} from '../../constants/enums';
import {getUserProperties, isGuest, isSchoolUser, isTeacher} from '../../utils/userUtility';
import {customEvent} from '../../utils/analyticsUtil';

class DialogStore {
    @observable showUploadDialog = false;
    @observable showRecordDialog = false;
    @observable showFilePreviewDialog = false;
    @observable showSaveLessonConfirm = false;
    @observable showUploadsInProgressConfirm = false;
    @observable showWhatNextConfirm = false;
    @observable showNotAssignedConfirm = false;
    @observable showNewLessonAfterConfirm = false;
    @observable showSetProjectNameDialog = false;
    @observable showRecordingDisable = false;
    @observable showResizableDialog = false;
    @observable inviteStudent = false;
    @observable showDiscoverDialog = false;
    @observable showSelectionDialog = false;
    @observable showUserIsAlreadyRegisteredDialog = false;
    @observable showNoStudentsDialog = false;
    @observable showPromoDialog = false;
    @observable promoTestType = PromoType.None;
    @observable showAccessDialog = false;
    @observable showAssignmentCreated = false;
    @observable showPricingDialog = false;
    @observable openProfilePopup = null;
    @observable showSetFolderNameDialog = false;
    @observable showDemoBookDialog = false;

    @observable dialogs = new Map();

    constructor() {
        runInAction(() => {
            Object.keys(DialogTypes).forEach(key => {
                this.dialogs.set(DialogTypes[key], false);
            });
            //If there's a dialog that we want default to be true, need to set it specifically here
            //For example : this.dialogs.set(DialogTypes.AddGroup, true);
        });
    }

    @action setProfilePopupAction = (action) => {
        this.openProfilePopup = action;
    }

    @action openDialog = (dialogType) => {
        this.dialogs.set(dialogType, true);
    }

    @action closeDialog = (dialogType) => {
        this.dialogs.set(dialogType, false);
    }

    @action resetAllDialogs = () => {
        this.dialogs.keys().forEach(key=> {
            this.dialogs.set(key, false);
        });
    }

    getDialogState = (dialogType) => {
        return this.dialogs.get(dialogType);
    }

    @action openInviteStudent = () => {
        this.inviteStudent = true;
    };

    @action closeInviteStudent = () => {
        this.inviteStudent = false;
    };

    @action openDiscoverDialog = () => {
        this.showDiscoverDialog = true;
    };

    @action closeDiscoverDialog = () => {
        this.showDiscoverDialog = false;
    };

    @action openSelectionDialog = () => {
        this.showSelectionDialog = true;
    };

    @action closeSelectionDialog = () => {
        this.showSelectionDialog = false;
    };

    @action openUploadDialog = () => {
        this.showUploadDialog = true;
    };
    @action closeUploadDialog = () => {
        this.showUploadDialog = false;
    };

    @action openRecordDialog = () => {
        this.showRecordDialog = true;
    };
    @action closeRecordDialog = () => {
        this.showRecordDialog = false;
    };
    @action toggleRecordDialog = () => {
        this.showRecordDialog = !this.showRecordDialog;
    };

    @action openFilePreviewDialog = () => {
        this.showFilePreviewDialog = true;
    };
    @action closeFilePreviewDialog = () => {
        this.showFilePreviewDialog = false;
    };

    @action openNotAssignedConfirm = () => {
        this.showNotAssignedConfirm = true;
    };
    @action closeNotAssignedConfirm = () => {
        this.showNotAssignedConfirm = false;
    };

    @action setNewLessonAfterConfirm = (val) => {
        this.showNewLessonAfterConfirm = val;
    };
    @action openSaveLessonConfirm = (openNew = true) => {
        this.showNewLessonAfterConfirm = openNew;
        this.showSaveLessonConfirm = true;
    };
    @action closeSaveLessonConfirm = () => {
        this.showSaveLessonConfirm = false;
    };

    @action openUploadsInProgressConfirm = () => {
        this.showUploadsInProgressConfirm = true;
    };
    @action closeUploadsInProgressConfirm = () => {
        this.showUploadsInProgressConfirm = false;
    };

    @action openWhatNextConfirm = () => {
        this.showWhatNextConfirm = true;
    };
    @action closeWhatNextConfirm = () => {
        this.showWhatNextConfirm = false;
    };

    @action openSetProjectNameDialog = () => {
        this.showSetProjectNameDialog = true;
    };
    @action closeSetProjectNameDialog = () => {
        this.showSetProjectNameDialog = false;
    };

    @action openRecordingDisable = () => {
        this.showRecordingDisable = true;
    };
    @action closeRecordingDisable = () => {
        this.showRecordingDisable = false;
    };

    @action openResizableDialog = () => {
        if (this.showResizableDialog) {
            this.closeResizableDialog();
            mainStore.setStudioDrawerState(StudioDrawerState.None);
            setTimeout(action(() => {
                this.showResizableDialog = true;
            }), 500);
        } else {
            this.showResizableDialog = true;
        }
    };
    @action closeResizableDialog = () => {
        this.showResizableDialog = false;
    };

    @action openUserIsAlreadyRegisteredDialog = () => {
        this.showUserIsAlreadyRegisteredDialog = true;
    };
    @action closeUserIsAlreadyRegisteredDialog = () => {
        this.showUserIsAlreadyRegisteredDialog = false;
    };

    @action openNoStudentsDialog = () => {
        this.showNoStudentsDialog = true;
    };
    @action closeNoStudentsDialog = () => {
        this.showNoStudentsDialog = false;
    };
    @action submitNoStudentsDialog = () => {
        this.showNoStudentsDialog = false;
        this.inviteStudent = true;
    };

    @action openPromoDialog = async () => {
        const promoTestVariant = await googleOptimizeStore.getPromoTestVariant();
        const variantAsInt = parseInt(promoTestVariant, 10);
        runInAction(() => {
            if (!isNaN(variantAsInt) && variantAsInt !== this.promoTestType) {
                this.promoTestType = variantAsInt;
            }
            customEvent(events.CustomEvent, {category: category.Promo, action: actions.Open, label: this.promoTestType});
            this.showPromoDialog = true;
        });
    };
    @action closePromoDialog = () => {
        customEvent(events.CustomEvent, {category: category.Promo, action: actions.Close, label: this.promoTestType});
        this.showPromoDialog = false;
    };

    startPromoDialogTimeout = () => {
        if (this.promoDialogTimeout) {
            return;
        }
        const duration = isGuest() ? 20 * 1000 : 45 * 1000;
        this.promoDialogTimeout =
            setTimeout(() => {
                if (isGuest() || (isTeacher() && !isSchoolUser() && !manageStore.hasPayingStudent && !onBoardingStore.showOnboarding && !getUserProperties().promo_11_19)) {
                    customEvent(events.CustomEvent, {category: category.Promo, action: actions.OpenDialog, label: (isGuest() ? 'guest' : 'teacher')});
                    this.openPromoDialog();
                }
            }, duration);
    };

    @action openShowAssignmentCreated = () => {
        this.showAssignmentCreated = true;
    };

    @action closeShowAssignmentCreated = () => {
        this.showAssignmentCreated = false;
    };

    @action openAccessDialog = () => {
        customEvent(events.CustomEvent, {category: category.Discover, action: actions.OpenAccessDialog});
        this.showAccessDialog = true;
    };

    @action closeAccessDialog= () => {
        customEvent(events.CustomEvent, {category: category.Discover, action: actions.CloseAccessDialog});
        this.showAccessDialog = false;
    };

    @action openPricingDialog = () => {
        this.showPricingDialog = true;
    };

    @action closePricingDialog= () => {
        this.showPricingDialog = false;
    };

    @action openSetFolderNameDialog = () => {
        this.showSetFolderNameDialog = true;
    };

    @action closeSetFolderNameDialog = () => {
        this.showSetFolderNameDialog = false;
    };

    @action openDemoBookDialog = () => {
        this.showDemoBookDialog = true;
    };

    @action closeDemoBookDialog = () => {
        this.showDemoBookDialog = false;
    };
}

const dialogStore = new DialogStore();
export default dialogStore;